import React from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline"

const StepNavigation = ({
  currentStep,
  onPrevious,
  onNext,
  onGenerate,
  formData,
  packs,
  models,
  errors,
}) => {
  // Get current step message (error or info)
  const getCurrentStepMessage = () => {
    switch (currentStep) {
      case 1:
        if (!formData.packId) {
          return {
            type: "error",
            message: "Please select a license pack to continue"
          }
        }
        return {
          type: "info",
          message: "Choose the pack where your licenses will be organized"
        }
      case 2:
        if (Object.keys(errors).length > 0) {
          return {
            type: "error",
            message: "Please fix all validation errors to continue"
          }
        }
        return {
          type: "info",
          message: "Configure the license details and generation settings"
        }
      case 3:
        return {
          type: "info",
          message: "Review your settings and generate the licenses"
        }
      default:
        return null
    }
  }

  // Check if next button should be disabled
  const isNextDisabled = () => {
    let disabled = false
    switch (currentStep) {
      case 1:
        disabled = !formData.packId || packs.length === 0
        break
      case 2:
        disabled = Object.keys(errors).length > 0
        break
      default:
        disabled = false
    }
    return disabled
  }

  const currentMessage = getCurrentStepMessage()

  return (
    <div className="bg-admin_paper/50 border-b border-admin_bordered">
      <div className="px-64 py-4 flex justify-between items-center">
        {/* Left side - Previous button */}
        <div className="flex-shrink-0">
          {currentStep > 1 ? (
            <Button variant="admin_secondary" onClick={onPrevious}>
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Previous
            </Button>
          ) : (
            <div className="w-24" />
          )}
        </div>

        {/* Center - Message (error or info) */}
        <div className="flex-1 flex justify-center items-center px-4">
          {currentMessage && (
            <div className={`flex items-center space-x-2 ${
              currentMessage.type === 'error' ? 'text-admin_error' : 'text-admin_text_secondary'
            }`}>
              {currentMessage.type === 'error' ? (
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
              ) : (
                <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
              )}
              <Typography
                variant="admin_txt1"
                className={`text-center whitespace-nowrap ${
                  currentMessage.type === 'error' ? 'text-admin_error' : 'text-admin_text_secondary'
                }`}
              >
                {currentMessage.message}
              </Typography>
            </div>
          )}
        </div>

        {/* Right side - Next/Generate button */}
        <div className="flex-shrink-0">
          {currentStep < 2 && (
            <Button
              variant="admin_primary"
              onClick={onNext}
              disabled={isNextDisabled()}
            >
              Next
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          )}

          {currentStep === 2 && (
            <Button
              variant="admin_primary"
              onClick={onNext}
              disabled={isNextDisabled()}
            >
              Proceed to Review
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          )}

          {currentStep === 3 && (
            <Button variant="admin_primary" onClick={onGenerate}>
              <DocumentDuplicateIcon className="w-5 h-5 mr-2" />
              Generate {(() => {
                const count = formData.activationCodeType === "explicit" 
                  ? formData.customCodesCount 
                  : formData.count
                return `${count} License${count !== 1 ? "s" : ""}`
              })()} 
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default StepNavigation
